/**
 * English copy — the source of truth for the site's text.
 *
 * Marketing copy is lifted from "Rickey Landing Ink.dc.html" and follows the
 * voice rules in "Rickey Brand Kit.dc.html". Rewriting it here would put the
 * site out of step with the Instagram captions and the app, so changes should
 * go back into the brand documents too.
 *
 * The voice: a good bartender who is not performing. Short sentences. A
 * position, stated plainly — "one cube, not four". British-flat rather than
 * American-enthusiastic. Never a pun about spirits, never "cheers", never an
 * exclamation mark.
 *
 * The English text is also the legally binding version of the notices — see
 * docs/legal.html.
 */
const en = {
  meta: {
    title: 'Rickey — tonight’s menu, from your shelf',
    description:
      'Tell Rickey what you own and it reads back what you can pour tonight — measures, order and timings, scaled to how many you are making.',
  },

  nav: {
    whatItDoes: 'What it does',
    free: 'What it costs',
    faq: 'Questions',
    cta: 'App Store',
    skipToContent: 'Skip to content',
    menu: 'Menu',
    closeMenu: 'Close menu',
    languageLabel: 'Language',
  },

  hero: {
    /* Caveat, so it reads as an aside rather than a claim. */
    eyebrow: 'twelve bottles, no idea what to make',
    headlineA: 'Tonight’s menu,',
    headlineB: 'from your shelf.',
    body: 'Tell Rickey what you own. It reads back what you can pour tonight — measures, order and timings.',
    cta: 'Get it free on the App Store',
    note: 'no account until you want one',
    productHunt: 'Rickey on Product Hunt',
  },

  app: {
    time: '23:04',
    handLabel: 'tonight’s',
    title: 'Signature Drinks',
    pourable: '6 pourable',
    byFamily: 'By glass family',
    rows: [
      {
        no: '01',
        name: 'House Rickey',
        spec: 'Bourbon · Lime juice · Soda water',
        abv: '13',
        family: 'Highball',
      },
      {
        no: '02',
        name: 'Boulevardier',
        spec: 'Rye whiskey · Campari · Sweet vermouth',
        abv: '24',
        family: 'Rocks',
      },
      {
        no: '03',
        name: 'Daiquiri',
        spec: 'White rum · Lime juice · Simple syrup',
        abv: '21',
        family: 'Coupe',
      },
      {
        no: '04',
        name: 'Paloma',
        spec: 'Tequila blanco · Lime juice · Grapefruit soda',
        abv: '12',
        family: 'Highball',
      },
      {
        no: '05',
        name: 'Espresso Martini',
        spec: 'Vodka · Espresso · Coffee liqueur',
        abv: '18',
        family: 'Shaken',
      },
      { no: '06', name: 'Gimlet', spec: 'Gin · Lime cordial', abv: '20', family: 'Coupe' },
    ],
    tabs: ['Menu', 'Saved', 'Shelf', 'Buy', 'You'],
  },

  /*
   * Numbers about the app, and the app is what decides them.
   *
   * This said 640 recipes while the bundle held 529. Nobody lied — the figure
   * simply stopped being true and nothing here could notice, because the
   * catalogue lives in the app repo and this file is a different codebase.
   *
   * test/unlocks/published_numbers_test.dart in the app now asserts 529 and
   * points back at this line, so a catalogue change fails there rather than
   * quietly making this wrong. Change them together.
   */
  stats: [
    { value: '529', label: 'recipes, measured' },
    { value: '12', label: 'bottles is plenty' },
    { value: '0', label: 'ads, ever' },
  ],

  how: {
    kicker: 'how it goes',
    steps: [
      {
        no: '01',
        title: 'Say what you own.',
        body: 'Tick your bottles once — spirits, vermouth, bitters, the tonic in the door. Twelve is plenty.',
      },
      {
        no: '02',
        title: 'Read tonight’s menu.',
        body: 'Only drinks you can actually make, filed by glass, with the measures already scaled to how many you’re pouring.',
      },
      {
        no: '03',
        title: 'Pour it, step by step.',
        body: 'One measure at a time, timings counted, in the right order — no scrolling back up a recipe with wet hands.',
      },
      {
        no: '04',
        title: 'Then it remembers.',
        body: 'Every pour lands in your bar book with a note, so the second time is better than the first.',
      },
    ],
  },

  pour: {
    kicker: 'the part nobody else does',
    headlineA: 'One number,',
    headlineB: 'at arm’s length.',
    body: 'Pouring mode shows the measure, not a picture of a shaker. The amount is the biggest thing on the screen, the gauge fills as the glass does, and the timings are counted for you — readable from across a kitchen with wet hands.',
    claims: ['Scales to six servings', 'Works offline', 'Dark by default at night'],
    demo: {
      step: 'step 02 · pour',
      amount: '1½ oz',
      of: 'of rye whiskey',
      inGlass: '1½ oz in the glass',
      whenDone: '3½ oz when it’s done',
      title: 'Pour the rye',
      body: 'Down the side of the glass so it chills on the way in.',
      nextLabel: 'Next',
      nextTitle: 'Add Campari and vermouth',
      done: 'Done, next step',
    },
  },

  pricing: {
    kicker: 'what it costs',
    name: 'Free',
    /* A price is a value, and values are the handwritten voice. */
    price: 'all of it',
    tagline: 'every feature, for everyone',
    blurb:
      'There is no paid tier and there is not going to be one. Batch maths, host mode, the bar book, unlimited saves — the lot, on every phone you sign in on.',
    items: [
      'Your whole shelf, unlimited bottles',
      'Everything pourable right now',
      'Every recipe and the step-by-step pour',
      'The shopping list, with notes and quantities',
      'Unlimited saved drinks',
      'Batch and party maths, up to forty guests',
      'Host mode — hand a guest the phone',
      'One bottle away, and every substitution',
      'The bar book: ratings, notes and history',
    ],
    note: 'An account is optional. It only matters if you want the same bar book on a second phone.',
  },

  faq: {
    kicker: 'questions',
    items: [
      {
        q: 'Do I need to own a lot of bottles?',
        a: 'No — that is rather the point. Six bottles already unlocks around thirty drinks, and Rickey tells you which single bottle would unlock the most next.',
      },
      {
        q: 'Where do the recipes come from?',
        a: 'A measured catalogue of classics, cross-checked against the CocktailDB. Photographs are optional and sit behind a button, because they arrive in every lighting and style imaginable and would make the app look like a scrapbook.',
      },
      {
        q: 'Does it work without signal?',
        a: 'Yes. Your shelf, your saved drinks and every recipe you have opened live on the phone. Only new drinks and photos need the internet.',
      },
      {
        q: 'What does it cost?',
        a: 'Nothing. Every feature is free, for everyone — there is no paid tier and there is not going to be one. An account is optional, and only matters if you want the same bar book on a second phone.',
      },
      {
        q: 'Where can I get it?',
        a: 'Rickey is on the App Store now, free, for iPhone and iPad. An Android version is being worked on and does not have a date.',
      },
    ],
  },

  finalCta: {
    headlineA: 'Pour something',
    headlineB: 'good tonight.',
    cta: 'Get it free on the App Store',
  },

  waitlist: {
    badge: 'not out yet',
    title: 'Rickey isn’t poured yet.',
    body: 'Leave your address and you get one message the day it reaches the App Store and Google Play. One message. Nothing else.',
    emailLabel: 'Your email address',
    emailPlaceholder: 'you@example.com',
    submit: 'Put me on the list',
    submitting: 'One moment…',
    close: 'Close',
    privacyNote: 'We only use your address to tell you the app is out.',
    privacyLink: 'How we handle your data',
    successTitle: 'You’re on the list.',
    successBody:
      'There is a confirmation on its way. We will write once more, the day it launches.',
    successDone: 'Close',
    errors: {
      missing_email: 'Enter your email address.',
      invalid_email: 'That does not look like a valid email address.',
      email_too_long: 'That email address is too long.',
      unknown_product: 'Something went wrong on our side. Try again.',
      rate_limited: 'Too many attempts. Wait a few minutes and try again.',
      spam_detected: 'Something went wrong. Try again.',
      send_failed: 'We could not save that just now. Try again, or write to hola@buildpassing.com.',
      server_error:
        'Something went wrong on our side. Try again, or write to hola@buildpassing.com.',
      network: 'We could not reach the server. Check your connection and try again.',
    },
  },

  footer: {
    tagline: 'drink like you mean it',
    builtBy: 'Built by',
    /* Plain, not preachy — the brand does not lecture. */
    ageNote: '18+. Rickey is a reference for people who already drink.',
    columns: { product: 'Product', legal: 'Legal', company: 'Company' },
    links: {
      whatItDoes: 'What it does',
      free: 'What it costs',
      faq: 'Questions',
      waitlist: 'Get it on the App Store',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Policy',
      deleteAccount: 'Delete your account',
      support: 'Support',
      contact: 'Say hello',
      instagram: 'Instagram',
      buildpassing: 'BuildPassing',
    },
    rights: 'All rights reserved.',
  },

  notFound: {
    title: 'Nothing poured here.',
    body: 'The link may be out of date, or we may have moved it. The front page is a good place to start.',
    cta: 'Back to the front page',
  },

  legal: {
    lastUpdated: 'Last updated',
    effectiveDate: 'Effective date',
    contents: 'Contents',
    questionsTitle: 'Questions about this document?',
    questionsBody: 'Write to us and a person will answer.',
    bindingNotice:
      'This document is also available in Spanish. If the two versions disagree, the English version governs.',
  },
};

/* Deliberately not `as const`. A literal type here would force the Spanish
   bundle to repeat the English strings exactly, which is the opposite of what
   we want — we need the same *shape*, not the same words. */
export default en;
